import React, { useState } from "react";

import { Trash2 } from "lucide-react";
import { useDispatch } from "react-redux";

import { Button } from "@components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { useRemoveFile } from "@hooks/mutations/use-files";

import { setMessage } from "@redux/notification";

import { stripBundlePrefix } from "@utils/bundle-name";
import { cn } from "@utils/ui";

import { ConfirmationModal } from "../../ConfirmationModal";
import { FilePreviewData } from "../types/data-table";

type DeleteFileConfirmationProps = {
  file: FilePreviewData;
  /** Icon-only, with the label as a title tooltip instead of visible text —
   * for compact action rows (Chronological/Timeline views). */
  iconOnly?: boolean;
};

export const DeleteFileConfirmation = ({
  file,
  iconOnly = false,
}: DeleteFileConfirmationProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();

  const removeFile = useRemoveFile();

  const handleDelete = async () => {
    try {
      await removeFile.mutateAsync(file.id);

      dispatch(
        setMessage({
          content: `File "${stripBundlePrefix(file.name)}" deleted successfully`,
          type: "success",
        })
      );

      setIsOpen(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete file";
      dispatch(
        setMessage({
          content: errorMessage,
          type: "error",
        })
      );
    }
  };

  const trigger = (
    <Button
      variant="ghost"
      size={iconOnly ? "icon" : "sm"}
      className={cn(
        "text-destructive hover:text-destructive",
        iconOnly ? "size-8 w-auto p-1" : "w-auto p-1"
      )}
      // A Tooltip can't wrap this button here — it isn't Slot-compatible, so
      // nesting it inside ConfirmationModal's own asChild trigger would swallow
      // the click that's supposed to open the dialog. iconOnly instead renders
      // its own Tooltip-wrapped trigger below and drives `open` directly.
      onClick={iconOnly ? () => setIsOpen(true) : undefined}
    >
      <Trash2 className="size-4" />
      {!iconOnly && "Delete File"}
    </Button>
  );

  return (
    <>
      <ConfirmationModal
        type="warning"
        icon={<Trash2 className="size-14 text-destructive" />}
        title="Delete File"
        description={`Are you sure you want to delete "${stripBundlePrefix(file.name)}"? This action cannot be undone.`}
        confirmButtonText={removeFile.isPending ? "Deleting..." : "Yes, Delete"}
        cancelButtonText="No, Cancel"
        onConfirm={handleDelete}
        isLoading={removeFile.isPending}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        {iconOnly ? undefined : trigger}
      </ConfirmationModal>

      {iconOnly && (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent>Delete File</TooltipContent>
        </Tooltip>
      )}
    </>
  );
};
